import { useState } from 'react';
import type { MouseEvent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { nameDataset } from '@/entities/dataset';
import type { CatalogueNames } from '@/entities/dataset';
import { DatasetKindIcon, useDatasetNames } from '@/features/catalog-browser';
import { useConnections } from '@/features/connections';
import { useContracts } from '@/features/contracts';
import { useIncidentTickets } from '@/features/itsm';
import { useBindings, useRules } from '@/features/rules';
import { useCursorStack } from '@/shared/api/paging';
import { INCIDENT_KINDS } from '@/shared/api/generated/observability';
import type { Incident, Severity } from '@/shared/api/generated/observability';
import type { IncidentTicketSummary } from '@/shared/api/generated/itsm';
import { ageText } from '@/shared/model/age';
import { personName } from '@/shared/model/person';
import { SCREEN_PATHS } from '@/shared/routes';
import { Alert } from '@/shared/ui/Alert';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SidePanel } from '@/shared/ui/SidePanel';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import {
  INCIDENT_QUEUE_CAP,
  useAcknowledgeIncident,
  useIncidentQueue,
  useIncidents,
  useResolveIncident,
} from '../api/incidents';
import {
  canAcknowledge,
  canResolve,
  groupForQueue,
  INCIDENT_FILTERS,
} from '../model/incidents';
import type { IncidentFilter } from '../model/incidents';

/* The incident queue as the redesign draws it: state pills with their counts, a kind and a severity
 * select, the active incidents grouped by severity as cards, and one incident opened in a panel over
 * the list.
 *
 * **The panel, not a page, is where a card leads**, because triage is reading one and moving to the
 * next; the list behind stays live. The incident's own page — routing, delivery attempts, ticket jobs
 * — is still one link away inside the panel, for the review that needs all of it.
 *
 * Every card carries its severity as a word in the group heading as well as a hue — WCAG 1.4.1. */

const SEVERITIES: readonly Severity[] = ['critical', 'warning', 'info'];

/** The pill's count: exact, or "N+" where the read it came from was cut off. */
function countText(count: number, more: boolean): string {
  return more ? `${count.toLocaleString()}+` : count.toLocaleString();
}

/* What the incident is about, in words. A dataset by its catalogue name; a contract, a rule binding
 * or a source by their own names, each from the one listing that names it — read only when an
 * incident about that kind of subject is on screen. */
function ContractName({ id }: { readonly id: string }): ReactElement {
  const { t } = useTranslation();
  const contracts = useContracts();
  const name = contracts.data?.items.find((each) => each.id === id)?.name;
  return (
    <>
      {t('incidents.subjectOf.contract', {
        name: name ?? t('incidents.subjectOf.unnamed'),
      })}
    </>
  );
}

function BindingName({ id }: { readonly id: string }): ReactElement {
  const { t } = useTranslation();
  const bindings = useBindings();
  const rules = useRules('');
  const ruleId = bindings.data?.items.find((each) => each.id === id)?.rule_id;
  const name = rules.data?.items.find((each) => each.id === ruleId)?.name;
  return (
    <>
      {t('incidents.subjectOf.ruleBinding', {
        name: name ?? t('incidents.subjectOf.unnamed'),
      })}
    </>
  );
}

function ConnectionName({ id }: { readonly id: string }): ReactElement {
  const { t } = useTranslation();
  const connections = useConnections();
  const name = connections.data?.items.find((each) => each.id === id)?.name;
  return (
    <>
      {t('incidents.subjectOf.connection', {
        name: name ?? t('incidents.subjectOf.unnamed'),
      })}
    </>
  );
}

function SubjectText({
  incident,
  names,
}: {
  readonly incident: Incident;
  readonly names: CatalogueNames | null;
}): ReactElement {
  const { t } = useTranslation();
  switch (incident.subject_kind) {
    case 'dataset': {
      const naming = nameDataset(incident.subject_id, names);
      return naming.kind === 'named' ? (
        <>
          <DatasetKindIcon datasetId={incident.subject_id} />
          {naming.name}
        </>
      ) : (
        <>{t('incidents.subjectOf.unlisted')}</>
      );
    }
    case 'contract':
      return <ContractName id={incident.subject_id} />;
    case 'rule_binding':
      return <BindingName id={incident.subject_id} />;
    case 'connection':
      return <ConnectionName id={incident.subject_id} />;
    case 'reconciliation_definition':
      return <>{t('incidents.subjectOf.reconciliation')}</>;
  }
}

/** Whether this incident produced a ticket, as a word — or nothing while the page-wide read has not
 * answered, which is not the same as the incident having no ticket. */
function ticketReading(
  incident: Incident,
  tickets: ReadonlyMap<string, IncidentTicketSummary> | undefined,
): {
  readonly kind: 'none' | 'link' | 'text';
  readonly tone?: 'muted' | 'bad';
  readonly key?: string;
  readonly href?: string;
} | null {
  if (tickets === undefined) return null;
  const summary = tickets.get(incident.id);
  if (summary === undefined) return { kind: 'none', tone: 'muted' };
  if (summary.ticket !== null) {
    return {
      kind: 'link',
      key: summary.ticket.external_key,
      href: summary.ticket.external_url,
    };
  }
  return {
    kind: 'text',
    tone: summary.state === 'failed' ? 'bad' : 'muted',
    key: `incidents.ticketWord.${summary.state}`,
  };
}

function TicketText({
  incident,
  tickets,
}: {
  readonly incident: Incident;
  readonly tickets: ReadonlyMap<string, IncidentTicketSummary> | undefined;
}): ReactElement | null {
  const { t } = useTranslation();
  const reading = ticketReading(incident, tickets);
  if (reading === null) return null;
  if (reading.kind === 'link') {
    return (
      <a
        className="lq-ic__ticket lq-ic__ticket--link"
        href={reading.href}
        target="_blank"
        rel="noreferrer"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        {reading.key}
      </a>
    );
  }
  return (
    <span className="lq-ic__ticket" data-tone={reading.tone}>
      {reading.kind === 'none' ? t('incidents.ticketWord.none') : t(reading.key ?? '')}
    </span>
  );
}

function IncidentCard({
  incident,
  names,
  tickets,
  now,
  selected,
  onSelect,
  onActed,
}: {
  readonly incident: Incident;
  readonly names: CatalogueNames | null;
  readonly tickets: ReadonlyMap<string, IncidentTicketSummary> | undefined;
  readonly now: Date;
  readonly selected: boolean;
  readonly onSelect: () => void;
  readonly onActed: (incident: Incident, action: 'acknowledged' | 'resolved') => void;
}): ReactElement {
  const { t } = useTranslation();
  const acknowledge = useAcknowledgeIncident();
  const resolve = useResolveIncident();
  const failure = acknowledge.error ?? resolve.error;

  const act =
    (action: 'acknowledged' | 'resolved') =>
    (event: MouseEvent): void => {
      /* The buttons sit on the card that opens the panel; acting must not also open it. */
      event.stopPropagation();
      const mutation = action === 'acknowledged' ? acknowledge : resolve;
      mutation.mutate(incident.id, {
        onSuccess: (updated) => {
          onActed(updated, action);
        },
      });
    };

  return (
    <article
      className="lq-ic"
      data-selected={selected ? 'true' : undefined}
      onClick={onSelect}
    >
      <div className="lq-ic__main">
        <div className="lq-ic__title">
          {/* The kind is the card's name and the control that opens it, so a keyboard reader reaches
              the panel the way a pointer does — by the card's heading. */}
          <button
            type="button"
            className="lq-ic__kind"
            aria-expanded={selected}
            onClick={(event) => {
              event.stopPropagation();
              onSelect();
            }}
          >
            {t(`incidents.kind.${incident.kind}`)}
          </button>
          <span className="lq-ic__subject">
            <SubjectText incident={incident} names={names} />
          </span>
        </div>
        <p className="lq-ic__summary">{incident.summary}</p>
        <div className="lq-ic__meta">
          <span>
            {t('incidents.openedAgo', {
              ago: ageText(t, incident.opened_at, now) ?? incident.opened_at,
            })}
          </span>
          <span className="lq-ic__state" data-state={incident.state}>
            {t(`incidents.stateWord.${incident.state}`)}
          </span>
          {incident.acknowledged_by === null ? null : (
            <span>
              {t('incidents.by.acknowledged', {
                who: personName(incident.acknowledged_by),
              })}
            </span>
          )}
          <TicketText incident={incident} tickets={tickets} />
        </div>
        {failure === null ? null : (
          <p className="lq-ic__error" role="alert">
            {failure.message}
          </p>
        )}
      </div>
      <div className="lq-ic__actions">
        {canAcknowledge(incident) ? (
          <button
            type="button"
            className="lq-ic__btn"
            disabled={acknowledge.isPending}
            onClick={act('acknowledged')}
          >
            {t('incidents.action.acknowledge')}
          </button>
        ) : null}
        {canResolve(incident) ? (
          <button
            type="button"
            className="lq-ic__btn"
            disabled={resolve.isPending}
            onClick={act('resolved')}
          >
            {t('incidents.action.resolve')}
          </button>
        ) : null}
      </div>
    </article>
  );
}

/* One incident in the panel: what happened, its three-step timeline, cause, evidence and ticket, and
 * the two transitions. */
function IncidentPanel({
  incident,
  names,
  tickets,
  now,
  onClose,
  onActed,
}: {
  readonly incident: Incident;
  readonly names: CatalogueNames | null;
  readonly tickets: ReadonlyMap<string, IncidentTicketSummary> | undefined;
  readonly now: Date;
  readonly onClose: () => void;
  readonly onActed: (incident: Incident, action: 'acknowledged' | 'resolved') => void;
}): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const acknowledge = useAcknowledgeIncident();
  const resolve = useResolveIncident();
  const failure = acknowledge.error ?? resolve.error;
  const ticket = ticketReading(incident, tickets);

  const steps = [
    {
      id: 'opened',
      done: true,
      tone: 'ink',
      who: t('incidents.panel.system'),
      when: ageText(t, incident.opened_at, now),
    },
    {
      id: 'acknowledged',
      done: incident.acknowledged_by !== null,
      tone: 'info',
      who:
        incident.acknowledged_by === null
          ? t('incidents.panel.nobodyYet')
          : personName(incident.acknowledged_by),
      when: null,
    },
    {
      id: 'resolved',
      done: incident.state === 'resolved',
      tone: 'ok',
      who:
        incident.resolved_by === null
          ? t('incidents.panel.notYet')
          : personName(incident.resolved_by),
      when: incident.resolved_at === null ? null : ageText(t, incident.resolved_at, now),
    },
  ] as const;

  const head = (
    <>
      <span className="lq-side__pill lq-ic__sev" data-severity={incident.severity}>
        {t(`incidents.severity.${incident.severity}`)}
      </span>
      <h2 className="lq-side__name">{t(`incidents.kind.${incident.kind}`)}</h2>
      <div className="lq-side__sub lq-mono">
        <SubjectText incident={incident} names={names} />
      </div>
    </>
  );

  return (
    <SidePanel label={t(`incidents.kind.${incident.kind}`)} onClose={onClose} head={head}>
      <p className="lq-ic__panelSummary">{incident.summary}</p>

      <div>
        <div className="lq-ic__label">{t('incidents.panel.timeline')}</div>
        <ol className="lq-ic__timeline">
          {steps.map((step) => (
            <li
              key={step.id}
              className="lq-ic__step"
              data-done={step.done ? 'true' : undefined}
            >
              <span className="lq-ic__dot" data-tone={step.done ? step.tone : 'none'} />
              <div>
                <div className="lq-ic__stepLabel">
                  {t(`incidents.timeline.${step.id}`)}
                </div>
                <div className="lq-ic__stepWho">{step.who}</div>
              </div>
              <span className="lq-ic__stepWhen">{step.when ?? ''}</span>
            </li>
          ))}
        </ol>
      </div>

      <dl className="lq-ic__facts">
        <dt>{t('incidents.panel.cause')}</dt>
        <dd className="lq-mono">{incident.cause}</dd>
        <dt>{t('incidents.fact.evidence')}</dt>
        <dd>
          {incident.evidence_kind === null || incident.evidence_ref === null
            ? t('incidents.panel.nothingToPointAt')
            : `${t(`incidents.evidenceKind.${incident.evidence_kind}`)} · ${incident.evidence_ref}`}
        </dd>
        <dt>{t('incidents.panel.ticket')}</dt>
        <dd>
          {ticket === null ? '—' : <TicketText incident={incident} tickets={tickets} />}
        </dd>
      </dl>

      {failure === null ? null : <Alert tone="error">{failure.message}</Alert>}

      <div className="lq-ic__panelActions">
        {canAcknowledge(incident) ? (
          <button
            type="button"
            className="lq-btn lq-btn--primary"
            disabled={acknowledge.isPending}
            onClick={() => {
              acknowledge.mutate(incident.id, {
                onSuccess: (updated) => {
                  onActed(updated, 'acknowledged');
                },
              });
            }}
          >
            {t('incidents.action.acknowledge')}
          </button>
        ) : null}
        {canResolve(incident) ? (
          <button
            type="button"
            className="lq-btn lq-btn--secondary"
            disabled={resolve.isPending}
            onClick={() => {
              resolve.mutate(incident.id, {
                onSuccess: (updated) => {
                  onActed(updated, 'resolved');
                },
              });
            }}
          >
            {t('incidents.action.resolve')}
          </button>
        ) : null}
        {incident.subject_kind === 'dataset' ? (
          <button
            type="button"
            className="lq-btn lq-btn--secondary"
            onClick={() => {
              navigate(`/datasets/${incident.subject_id}`);
            }}
          >
            {t('incidents.panel.openDataset')}
          </button>
        ) : null}
      </div>

      {/* The incident's own page keeps what a review needs beyond triage: who would be told, who was
          told, and every ticket attempt. */}
      <Link className="lq-ic__full" to={`${SCREEN_PATHS.incidents}/${incident.id}`}>
        {t('incidents.panel.full')}
      </Link>
    </SidePanel>
  );
}

export function IncidentQueue(): ReactElement {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<IncidentFilter>('active');
  const [kind, setKind] = useState<string | null>(null);
  const [severity, setSeverity] = useState<Severity | null>(null);
  /* The incident in the panel, held as the record itself so the panel keeps showing it after an
     action moves it out of the current filter — resolving from Active should not snap the panel shut
     on the reader who just pressed Resolve. */
  const [selected, setSelected] = useState<Incident | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const paging = useCursorStack();

  const queue = useIncidentQueue();
  /* The Resolved pill's count: the first page of resolved incidents, with "+" when there is more. */
  const resolvedHead = useIncidents({
    filter: 'resolved',
    kind: null,
    severity: null,
    cursor: null,
  });
  const incidents = useIncidents({ filter, kind, severity, cursor: paging.cursor });
  const tickets = useIncidentTickets(
    (incidents.data?.items ?? []).map((incident) => incident.id),
  );
  const names = useDatasetNames();
  const now = new Date();

  const active = queue.data ?? null;
  const atCap = active !== null && active.length >= INCIDENT_QUEUE_CAP;
  const counts: Partial<Record<IncidentFilter, string>> = {
    ...(active === null
      ? {}
      : {
          active: countText(active.length, atCap),
          open: countText(active.filter((each) => each.state === 'open').length, atCap),
          acknowledged: countText(
            active.filter((each) => each.state === 'acknowledged').length,
            atCap,
          ),
        }),
    ...(resolvedHead.data === undefined
      ? {}
      : {
          resolved: countText(
            resolvedHead.data.items.length,
            resolvedHead.data.nextCursor !== null,
          ),
        }),
  };

  const refilter = (change: () => void): void => {
    change();
    paging.reset();
  };

  const onActed = (incident: Incident, action: 'acknowledged' | 'resolved'): void => {
    setAnnouncement(
      t(`incidents.acted.${action}.title`, {
        incident: t(`incidents.kind.${incident.kind}`),
      }),
    );
    setSelected((current) => (current?.id === incident.id ? incident : current));
  };

  /* The panel's record, refreshed from the lists when they hold a newer copy of it. */
  const panel =
    selected === null
      ? null
      : ((incidents.data?.items ?? []).find((each) => each.id === selected.id) ??
        (active ?? []).find((each) => each.id === selected.id) ??
        selected);

  const page = incidents.data;
  const groups = page === undefined ? [] : groupForQueue(page.items);
  const filtered = kind !== null || severity !== null;

  return (
    <>
      <PageHeader
        title={t('incidents.title')}
        help={t('help.feature.incidents')}
        lede={t('incidents.subtitle')}
      />

      <div className="lq-stack">
        <div
          className="lq-ic__pills"
          role="group"
          aria-label={t('incidents.filter.state')}
        >
          {INCIDENT_FILTERS.map((each) => (
            <button
              key={each}
              type="button"
              className="lq-ic__pill"
              aria-pressed={each === filter}
              onClick={() => {
                refilter(() => {
                  setFilter(each);
                });
              }}
            >
              {t(`incidents.filter.${each}`)}
              {/* A space the flex row does not draw, so the accessible name reads "Open 3" rather
                  than "Open3". */}
              {counts[each] === undefined ? null : ' '}
              {counts[each] === undefined ? null : (
                <span className="lq-ic__pillCount">{counts[each]}</span>
              )}
            </button>
          ))}
        </div>

        <div className="lq-ic__selects">
          <select
            className="lq-ic__select"
            aria-label={t('incidents.filter.kind')}
            value={kind ?? ''}
            onChange={(event) => {
              refilter(() => {
                setKind(event.target.value === '' ? null : event.target.value);
              });
            }}
          >
            <option value="">{t('incidents.filter.everyKind')}</option>
            {INCIDENT_KINDS.map((each) => (
              <option key={each} value={each}>
                {t(`incidents.kind.${each}`)}
              </option>
            ))}
          </select>
          <select
            className="lq-ic__select"
            aria-label={t('incidents.filter.severity')}
            value={severity ?? ''}
            onChange={(event) => {
              const value = event.target.value;
              refilter(() => {
                setSeverity(
                  value === ''
                    ? null
                    : (SEVERITIES.find((each) => each === value) ?? null),
                );
              });
            }}
          >
            <option value="">{t('incidents.filter.anySeverity')}</option>
            {SEVERITIES.map((each) => (
              <option key={each} value={each}>
                {t(`incidents.severity.${each}`)}
              </option>
            ))}
          </select>
          {filtered ? (
            <button
              type="button"
              className="lq-ic__clear"
              onClick={() => {
                refilter(() => {
                  setKind(null);
                  setSeverity(null);
                });
              }}
            >
              {t('incidents.filter.clear')}
            </button>
          ) : null}
        </div>

        <p className="lq-visually-hidden" role="status">
          {announcement}
        </p>

        {incidents.isPending ? (
          <>
            <p role="status" className="lq-muted">
              {t('incidents.loading')}
            </p>
            <SkeletonRows rows={4} columns={4} />
          </>
        ) : null}

        {incidents.isError ? (
          <Alert tone="error">
            {t('incidents.unreachable', { detail: incidents.error.message })}
          </Alert>
        ) : null}

        {page !== undefined && groups.length === 0 ? (
          <div className="lq-ic__none">
            <div className="lq-ic__noneTitle">{t('incidents.none.title')}</div>
            <div className="lq-ic__noneBody">{t('incidents.none.body')}</div>
          </div>
        ) : null}

        {groups.map((group) => (
          <section key={group.severity} className="lq-ic__group">
            <div className="lq-ic__groupHead">
              <h2 className="lq-ic__groupPill" data-severity={group.severity}>
                {t(`incidents.severityGroup.${group.severity}`)}
              </h2>
              <span className="lq-ic__groupCount">{group.incidents.length}</span>
            </div>
            {group.incidents.map((incident) => (
              <IncidentCard
                key={incident.id}
                incident={incident}
                names={names.data ?? null}
                tickets={tickets.data}
                now={now}
                selected={panel?.id === incident.id}
                onSelect={() => {
                  setSelected(incident);
                }}
                onActed={onActed}
              />
            ))}
          </section>
        ))}

        {page?.atQueueCap === true ? (
          <p className="lq-muted lq-ic__note">
            {t('incidents.cappedNote', { cap: page.items.length })}
          </p>
        ) : null}

        {page !== undefined && (paging.canGoBack || page.nextCursor !== null) ? (
          <div className="lq-ic__pager">
            {paging.canGoBack ? (
              <button type="button" className="lq-ic__pageBtn" onClick={paging.back}>
                {t('incidents.pager.back')}
              </button>
            ) : null}
            <span className="lq-ic__page">
              {t('incidents.pager.page', { page: paging.page })}
            </span>
            {page.nextCursor === null ? null : (
              <button
                type="button"
                className="lq-ic__pageBtn"
                onClick={() => {
                  paging.forward(page.nextCursor);
                }}
              >
                {t('incidents.pager.next')}
              </button>
            )}
          </div>
        ) : null}
      </div>

      {panel === null ? null : (
        <IncidentPanel
          incident={panel}
          names={names.data ?? null}
          tickets={tickets.data}
          now={now}
          onClose={() => {
            setSelected(null);
          }}
          onActed={onActed}
        />
      )}
    </>
  );
}
